import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, KeyRound, ShieldAlert, Users } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function normalizeLabel(value) {
  return String(value || '—').replaceAll('_', ' ');
}

function SummaryCard({ title, value, detail }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{title}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </article>
  );
}

function ActivityBadge({ active }) {
  return (
    <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${active ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-zinc-200 bg-zinc-50 text-zinc-600'}`}>
      {active ? 'On' : 'Off'}
    </span>
  );
}

export default function InternalAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [debugError, setDebugError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setDebugError('');
    try {
      const { data } = await api.get('/admin/accounts?limit=25');
      setAccounts(data.accounts || []);
    } catch (requestError) {
      setError('Internal accounts could not be loaded.');
      if (process.env.NODE_ENV !== 'production') {
        setDebugError(requestError?.response?.data?.error || requestError?.message || '');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => ({
    total: accounts.length,
    onboardingComplete: accounts.filter((account) => account.onboarding_completed).length,
    withDigitalKey: accounts.filter((account) => (account.digital_key_account_count || 0) > 0).length,
    withRecentActivity: accounts.filter((account) => Boolean(account.last_activity_at)).length,
  }), [accounts]);

  return (
    <div data-testid="internal-accounts-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Accounts Overview" showDashboard={false} />

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Internal Accounts Overview</h1>
        <p className="mt-2 max-w-3xl text-sm text-zinc-600">Safe internal workspace overview for customer and testing organizations. This page only shows masked metadata, counts, and recent activity summaries.</p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="font-medium">Internal-only account overview</p>
            <p className="mt-1">No API keys, webhook secrets, raw evidence, OCR/MRZ data, document images, selfie images, or tokens are exposed here.</p>
          </div>
        </div>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard title="Organizations" value={summary.total} detail="Non-internal workspaces in the current internal overview." />
        <SummaryCard title="Onboarding complete" value={summary.onboardingComplete} detail="Organizations that completed workspace onboarding." />
        <SummaryCard title="With digital key" value={summary.withDigitalKey} detail="Organizations with at least one linked digital key account." />
        <SummaryCard title="Recent activity" value={summary.withRecentActivity} detail="Organizations with a known last activity timestamp." />
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Account rows</h2>
            <p className="mt-1 text-sm text-zinc-600">Each row stays in masked, internal-only metadata. Open a row for details and linked internal objects.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/internal/identities" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open identities</Link>
            <Link to="/internal/digital-key-accounts" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open digital key accounts</Link>
          </div>
        </div>

        {loading ? (
          <p className="mt-4 text-sm text-zinc-500">Loading internal accounts…</p>
        ) : error ? (
          <div className="mt-4 space-y-1 text-sm text-red-700">
            <p>{error}</p>
            {debugError && <p className="font-mono text-[10px] text-red-500">{debugError}</p>}
          </div>
        ) : accounts.length === 0 ? (
          <div className="mt-4 border border-dashed border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
            <p className="font-medium text-zinc-800">No accounts found.</p>
            <p className="mt-1">Once organizations exist, they will appear here with masked metadata and safe counts.</p>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[1280px] w-full text-sm">
              <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Organization</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Onboarding</th>
                  <th className="py-2 pr-4 font-medium">Members</th>
                  <th className="py-2 pr-4 font-medium">Identity records</th>
                  <th className="py-2 pr-4 font-medium">Digital key</th>
                  <th className="py-2 pr-4 font-medium">Login requests</th>
                  <th className="py-2 pr-4 font-medium">Last activity</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.organization_id} className="border-b border-zinc-100 last:border-b-0 align-top">
                    <td className="py-3 pr-4">
                      <Link to={`/internal/accounts/${account.organization_id}`} className="font-medium text-zinc-800 hover:text-black hover:underline">
                        {account.organization_name}
                      </Link>
                      <div className="mt-1 font-mono text-[10px] text-zinc-500">{account.organization_id}</div>
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">
                      <div>{normalizeLabel(account.workspace_type)}</div>
                      <div className="mt-1 text-xs text-zinc-500">{account.organization_type ? normalizeLabel(account.organization_type) : 'No organization type set'}</div>
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">
                      <ActivityBadge active={account.onboarding_completed} />
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">
                      <div>{account.member_count || 0}</div>
                      <div className="mt-1 text-xs text-zinc-500">{account.admin_member_count || 0} admin · {account.reviewer_member_count || 0} reviewer</div>
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">{account.identity_count || 0}</td>
                    <td className="py-3 pr-4 text-zinc-600">
                      <div>{account.digital_key_account_count || 0}</div>
                      <div className="mt-1 text-xs text-zinc-500">{account.active_digital_key_account_count || 0} active</div>
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">{account.login_request_count || 0}</td>
                    <td className="py-3 pr-4 text-zinc-600">{formatDate(account.last_activity_at)}</td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/internal/accounts/${account.organization_id}`} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
                          <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Open
                        </Link>
                        <Link to="/internal/identities" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
                          <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Identities
                        </Link>
                        <Link to="/internal/digital-key-accounts" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
                          <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Digital key
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
